import { PlusOutlined } from "@ant-design/icons"
import { Button, Checkbox, Form } from "antd"
import { useWatch } from "antd/es/form/Form"
import dayjs from "dayjs"
import { useEffect, useState, useMemo, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { receiveErrors } from "modules/errors/reducer"
import { modelSelector } from "store/selectors"
import { updateTrip, updatedSeasonal } from "store/trips"
import { Card } from "ui/Card"
import { DateRangeBlock } from "../components/scheduling/DateRangeBlock"
import { DaysAndHours } from "../components/scheduling/DaysAndHours"
import { LengthAndRates } from "../components/scheduling/LengthAndRates"
import { SeasonTypeSelector } from "../components/scheduling/SeasonTypeSelector"
import { GuestDetails } from "../components/scheduling/GuestDetails"
import { PRICING_TYPES, SEASON_TYPES } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { buildSchedulingPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"

// Price-specific form fields captured when a row is auto-saved on Next/Save
const PRICE_FIELDS = [
  "trip_duration", "trip_buffer_time",
  "price_per_person", "price_per_child", "price_per_infant", "price_per_passenger",
  "flat_rate", "included_guests",
  "children_allowed", "infants_allowed", "passengers_allowed", "extra_guests_allowed"
]

export const SchedulingStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const { form, stepSaveHandlersRef } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))
  const watchedFields = useWatch([], form)

  const [isSeasonalExperience, setIsSeasonalExperience] = useState(false)
  const [timeBlocks, setTimeBlocks] = useState([])
  const [isShowErrorMessage, setIsShowErrorMessage] = useState(false)
  const [localPrices, setLocalPrices] = useState([])
  const [expandedRowData, setExpandedRowData] = useState(null)
  const hasInitialized = useRef(false)
  const hasPricesInitialized = useRef(false)

  useEffect(() => {
    if (!trip?.id || hasInitialized.current) return

    const isSeasonal = trip.seasonal === true
    setIsSeasonalExperience(isSeasonal)

    if (isSeasonal && trip?.available_date_ranges?.length > 0) {
      const rangeValues = {}
      trip.available_date_ranges.forEach((range, index) => {
        if (range.start) rangeValues[`seasonStart_${index}`] = dayjs(range.start)
        if (range.end) rangeValues[`seasonEnd_${index}`] = dayjs(range.end)
      })
      form.setFieldsValue(rangeValues)
      setTimeBlocks(trip.available_date_ranges.map((_, i) => i))
    } else if (isSeasonal) {
      setTimeBlocks([0])
    }

    hasInitialized.current = true
  }, [form, trip])

  useEffect(() => {
    if (!trip?.id || hasPricesInitialized.current) return
    setLocalPrices(trip.trip_prices || [])
    hasPricesInitialized.current = true
  }, [trip])

  useEffect(() => {
    hasInitialized.current = false
    hasPricesInitialized.current = false
  }, [id])

  const handleSeasonChange = (e) => {
    const isSeasonal = e.target.value === SEASON_TYPES.SEASONAL
    setIsSeasonalExperience(isSeasonal)
    if (isSeasonal && timeBlocks.length === 0) setTimeBlocks([0])
    form.setFieldsValue({ seasonal: isSeasonal })
    dispatch(updatedSeasonal(isSeasonal))
  }

  const handleAddTimeBlock = () => {
    setTimeBlocks((prev) => {
      const next = prev.length ? Math.max(...prev) + 1 : 0
      return [...prev, next]
    })
  }

  const handleRemoveTimeBlock = (index) => {
    setTimeBlocks((prev) => prev.filter((i) => i !== index))
    form.setFieldsValue({ [`seasonStart_${index}`]: undefined, [`seasonEnd_${index}`]: undefined })
  }

  const getAllSelectedDateRanges = useMemo(
    () =>
      timeBlocks
        .map((blockIndex) => {
          const start = form.getFieldValue(`seasonStart_${blockIndex}`)
          const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
          return start && end ? { blockIndex, start, end } : null
        })
        .filter(Boolean),
    [form, timeBlocks, watchedFields]
  )

  const handlePriceSave = useCallback((priceData) => {
    setLocalPrices((prev) => {
      if (priceData.id) {
        return prev.map((p) => (p.id === priceData.id ? { ...p, ...priceData } : p))
      }
      const exists = prev.some((p) => p.tempId === priceData.tempId)
      if (exists) {
        return prev.map((p) => (p.tempId === priceData.tempId ? { ...p, ...priceData } : p))
      }
      return [...prev, priceData]
    })
  }, [])

  const handlePriceDelete = useCallback((record) => {
    setLocalPrices((prev) => {
      if (record.id) {
        return prev.map((p) => (p.id === record.id ? { id: p.id, _destroy: true } : p))
      }
      return prev.filter((p) => p.tempId !== record.tempId)
    })
  }, [])

  useEffect(() => {
    stepSaveHandlersRef.current["scheduling"] = async () => {
      setIsShowErrorMessage(true)

      // Auto-capture any currently open price row so clicking Next/Save doesn't lose edits
      let effectivePrices = localPrices
      const row = expandedRowData
      const isOpenServerPrice = row && typeof row.id === "number"
      const isOpenLocalPrice = row && row.tempId
      if (isOpenServerPrice || isOpenLocalPrice) {
        const allFormValues = form.getFieldsValue(true)
        const currentPricingType = form.getFieldValue("pricing_type") || PRICING_TYPES.PER_PERSON
        const captured = {}
        PRICE_FIELDS.forEach((key) => {
          if (allFormValues[key] !== undefined) captured[key] = allFormValues[key]
        })
        captured.pricing_type = currentPricingType

        if (isOpenServerPrice) {
          effectivePrices = localPrices.map((p) =>
            p.id === row.id ? { ...p, ...captured, id: p.id } : p
          )
        } else {
          const exists = localPrices.some((p) => p.tempId === row.tempId)
          effectivePrices = exists
            ? localPrices.map((p) => (p.tempId === row.tempId ? { ...p, ...captured } : p))
            : [...localPrices, { ...captured, tempId: row.tempId }]
        }
      }

      const selectedPricingType = form.getFieldValue("pricing_type") || trip?.pricing_type || PRICING_TYPES.PER_PERSON
      const activePrices = effectivePrices.filter(
        (p) => !p._destroy && !!p.pricing_type && p.pricing_type === selectedPricingType
      )
      if (activePrices.length === 0) {
        dispatch(
          receiveErrors({
            data: {
              message: {
                listing_rates: [t("experiences.scheduling.sections.length_and_rates.no_rates_error_message")]
              }
            },
            showToast: false
          })
        )
        throw new Error("No rates defined")
      }

      const values = form.getFieldsValue()
      const payload = buildSchedulingPayload(values, { isSeasonalExperience, timeBlocks, localPrices: effectivePrices })
      await dispatch(updateTrip(id, payload))
    }
    return () => {
      delete stepSaveHandlersRef.current["scheduling"]
    }
  }, [dispatch, id, form, stepSaveHandlersRef, isSeasonalExperience, timeBlocks, trip, t, localPrices, expandedRowData])

  return (
    <div style={{ maxWidth: "984px" }}>
      <Card className="mb-3xl" title={t("listings.tabs.scheduling.sections.seasons.title")}>
        <SeasonTypeSelector
          onChange={handleSeasonChange}
          value={isSeasonalExperience ? SEASON_TYPES.SEASONAL : SEASON_TYPES.ALL_YEAR}
        />
        {isSeasonalExperience && (
          <>
            {timeBlocks.map((blockIndex) => {
              const dateRange = trip?.available_date_ranges?.[blockIndex]
              return (
                <DateRangeBlock
                  key={blockIndex}
                  blockIndex={blockIndex}
                  onDelete={handleRemoveTimeBlock}
                  isSeasonalActive={isSeasonalExperience}
                  initialStartDate={dateRange?.start ? dayjs(dateRange.start) : undefined}
                  initialEndDate={dateRange?.end ? dayjs(dateRange.end) : undefined}
                  allRanges={getAllSelectedDateRanges}
                  timeBlocks={timeBlocks}
                />
              )
            })}
            <Button type="link" icon={<PlusOutlined />} onClick={handleAddTimeBlock} className="mt-m pl-0">
              {t("listings.tabs.scheduling.sections.seasons.add_time_block")}
            </Button>
            <Form.Item name="recurringYearly" valuePropName="checked" className="mt-l">
              <Checkbox>{t("listings.tabs.scheduling.sections.seasons.recurring_yearly")}</Checkbox>
            </Form.Item>
          </>
        )}
      </Card>

      <DaysAndHours form={form} />
      <GuestDetails form={form} />
      <LengthAndRates
        form={form}
        isShowErrorMessage={isShowErrorMessage}
        setIsShowErrorMessage={setIsShowErrorMessage}
        localPrices={localPrices}
        onPriceSave={handlePriceSave}
        onPriceDelete={handlePriceDelete}
        expandedRowData={expandedRowData}
        setExpandedRowData={setExpandedRowData}
      />
    </div>
  )
}
